import { useEffect, useRef } from 'react';
import AvatarImage from '@/components/common/AvatarImage';
import { FaUsers, FaBullhorn } from 'react-icons/fa';

export interface MentionItem {
  id: string;
  username: string;
  avatarUrl?: string | null;
  status?: string;
  isSpecial?: boolean;
  description?: string;
}

interface MentionAutocompleteProps {
  isOpen: boolean;
  query: string;
  members: MentionItem[];
  selectedIndex: number;
  onSelect: (username: string) => void;
  onClose: () => void;
}

export default function MentionAutocomplete({
  isOpen,
  query,
  members,
  selectedIndex,
  onSelect,
  onClose: _onClose,
}: MentionAutocompleteProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      const activeEl = containerRef.current.querySelector<HTMLElement>(`[data-index="${selectedIndex}"]`);
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  if (!isOpen || members.length === 0) return null;

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'online':
        return 'bg-green-500';
      case 'away':
      case 'idle':
        return 'bg-yellow-500';
      case 'dnd':
        return 'bg-brand-error';
      default:
        return 'bg-gray-400';
    }
  };

  return (
    <div
      ref={containerRef}
      className="absolute bottom-full left-0 mb-2 w-72 max-w-[90vw] max-h-64 overflow-y-auto bg-brand-surface/95 dark:bg-[#1f1c48]/95 backdrop-blur-xl border border-brand-outline-variant/70 rounded-2xl shadow-2xl z-30 p-1.5 animate-m3-dialog"
      role="listbox"
      aria-label="Mentions autocomplete"
    >
      <div className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-brand-outline dark:text-[#c4b5fd] border-b border-brand-outline-variant/40 mb-1 flex items-center justify-between">
        <span>Members matching "@{query}"</span>
        <span className="text-[10px] lowercase font-normal opacity-75">tab or enter to select</span>
      </div>

      {members.map((member, idx) => {
        const isSelected = idx === selectedIndex;
        return (
          <button
            key={member.id}
            data-index={idx}
            type="button"
            role="option"
            aria-selected={isSelected}
            onClick={() => onSelect(member.username)}
            className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-left transition-all duration-150 cursor-pointer ${
              isSelected
                ? 'bg-primary-600/30 text-white border border-primary-500/50 shadow-sm'
                : 'text-brand-on-surface dark:text-[#e0e7ff] hover:bg-white/8'
            }`}
          >
            {member.isSpecial ? (
              <div className="w-8 h-8 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-300 flex-shrink-0">
                {member.username === 'everyone' ? <FaUsers size={14} /> : <FaBullhorn size={14} />}
              </div>
            ) : (
              <div className="relative flex-shrink-0">
                <div className="w-8 h-8 rounded-full bg-primary-700 flex items-center justify-center overflow-hidden">
                  <AvatarImage
                    src={member.avatarUrl || undefined}
                    alt={member.username}
                    fallback={
                      <span className="text-white font-bold text-xs">
                        {member.username?.[0]?.toUpperCase() || 'U'}
                      </span>
                    }
                  />
                </div>
                {member.status && (
                  <div
                    className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-brand-surface dark:border-[#1f1c48] ${getStatusColor(
                      member.status,
                    )}`}
                  />
                )}
              </div>
            )}

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-xs sm:text-sm font-semibold truncate">
                  @{member.username}
                </span>
                {member.isSpecial && (
                  <span className="text-[10px] font-bold px-1.5 py-0.2 bg-amber-500/20 text-amber-300 rounded">
                    Notify All
                  </span>
                )}
              </div>
              {member.description && (
                <p className="text-[11px] text-brand-outline dark:text-[#c4b5fd] truncate">
                  {member.description}
                </p>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
